<template>
  <section class="grid grid-cols-12 gap-5">
    <div class="col-span-12 flex flex-col gap-5">
      <div class="flex justify-between flex-col md:flex-row items-center gap-3">
        <h4 class="capitalize">
          {{
            data.course.name?.concat(
              classroomSelected?.name ? ` - ${classroomSelected?.name}` : ""
            )
          }}
        </h4>
        <div
          v-if="hasClassroom"
          id="classroom-input"
          class="font-bold p-input-icon-right"
        >
          <AutoComplete
            id="classroom"
            v-model="classroomSelected"
            :suggestions="classrooms"
            :forceSelection="true"
            @complete="getClasses($event)"
            @item-select="setClassroomId(classroomSelected?.id)"
            :placeholder="$t('homeStudent.filters.classroom.placeholder')"
            optionLabel="name"
            :class="`w-96`"
          />

          <i v-if="!classroomSelected?.id" class="pi pi-search" />
          <i
            v-else
            class="pi pi-times cursor-pointer"
            @click.prevent="
              setClassroomId(undefined);
              classroomSelected = '';
            "
          />
        </div>
      </div>
      <div class="flex items-center justify-between mb-4">
        <h4 class="mb-2">
          {{ $t("stats.course.sidebar.general") }}
        </h4>
        <Button
          :label="$t('labels.generic', { pt: 'Baixar', en: 'Download' })"
          icon="pi pi-download"
          class="p-button-sm btn-primary h-9"
          :loading="btnLoading"
          @click="downloadCsv"
        />
      </div>
    </div>

    <div
      v-for="(item, i) in data.cards"
      :key="i"
      :class="['col-span-12', isAdaptive ? 'md:col-span-6' : '']"
    >
      <Card v-if="item" :data="item" />
    </div>

    <div
      :class="['col-span-12', isAdaptive ? 'md:col-span-6' : 'md:col-span-12']"
    >
      <Card :hasBorder="false" :isEmpty="true">
        <template #content>
          <div v-if="!loading">
            <h4 class="mb-2">
              {{ $t("labels.generic", { pt: "Alunos", en: "Students" }) }}
            </h4>
            <div
              v-if="students?.series?.length !== 0"
              class="flex justify-center gap-5 items-center"
            >
              <div :class="[isAdaptive ? 'w-8/12' : 'w-5/12']">
                <ApexChart
                  type="donut"
                  :options="students.options"
                  :series="students.series"
                ></ApexChart>
              </div>
              <div :class="['flex flex-col gap-5 w-/12']">
                <div v-for="(item, i) in students.legends" :key="i">
                  <div class="flex items-center gap-1">
                    <h5>{{ item.title }}</h5>
                    <span v-if="item.tooltip" v-tooltip="`${item.tooltip}`">
                      <v-icon name="hi-information-circle" :scale="0.8" />
                    </span>
                  </div>
                  <div class="flex items-center gap-3">
                    <p :class="`${item.color} h-2 w-2 rounded-full`"></p>
                    <h3>{{ item.number }}</h3>
                  </div>
                </div>
              </div>
            </div>
            <EmptyData
              :data="students?.series?.length === 0"
              :title="emptyState.title"
              :label="emptyState.description"
              :image="emptyState.image"
              imgSize="h-40"
            />
          </div>
        </template>
      </Card>
    </div>

    <div v-if="isAdaptive" class="col-span-12 md:col-span-6">
      <Card :hasBorder="false" :isEmpty="true">
        <template #content>
          <h4 class="mb-2">
            {{
              $t("labels.generic", {
                pt: "Alunos que desativaram a detecção",
                en: "Students who turned detection off",
              })
            }}
          </h4>
          <div
            v-if="students?.series?.length !== 0"
            class="grid grid-cols-12 items-center"
          >
            <div class="col-span-8">
              <ApexChart
                key="attention-chart"
                type="donut"
                :options="attention.options"
                :series="attention.series"
              ></ApexChart>
            </div>
            <div class="col-span-4 flex flex-col gap-5">
              <div v-for="(item, i) in attention.legends" :key="i">
                <div class="flex items-center gap-1">
                  <h5>{{ item.title }}</h5>
                  <h3 v-if="item.tooltip" v-tooltip.top="`${item.tooltip}`">
                    <v-icon name="hi-information-circle" :scale="0.8" />
                  </h3>
                </div>
                <div class="flex items-center gap-3">
                  <p :class="`${item.color} h-2 w-2 rounded-full`"></p>
                  <h3>{{ item.number }}</h3>
                </div>
              </div>
            </div>
          </div>
          <EmptyData
            :data="students?.series?.length === 0"
            :title="emptyState.title"
            :label="emptyState.description"
            :image="emptyState.image"
            imgSize="h-40 my-10"
          />
        </template>
      </Card>
    </div>
  </section>
</template>

<script lang="ts">
import { Classroom } from "@/models/classrooms.model";
import { GeneralInfo } from "@/models/stats.model";
import { ClassesService } from "@/views/Classes/Content/classes.service";
import { saveAs } from "file-saver";
import { defineAsyncComponent, defineComponent } from "vue";
import { StatsService } from "../../stats.service";
import { ChartData, colNames, exportOptions, Legends } from "./charts";
import { GeneralService } from "./general.service";
import { useThemeStore } from "@/store/theme";

const Card = defineAsyncComponent(() => import("../../Card/card.vue"));
export default defineComponent({
  name: "generalInfo",
  components: { Card },
  computed: {
    service(): GeneralService {
      return new GeneralService();
    },
    serviceClassrooms(): ClassesService {
      return new ClassesService();
    },
    isAdaptive(): boolean {
      return JSON.parse(localStorage.getItem("isAdaptiveStats") as string);
    },
  },
  data() {
    return {
      data: {
        course: {
          name: this.$route.params.courseName as any,
        },
        cards: [
          {
            title: " - ",
            info: this.$t("stats.course.cards.quiz"),
            icon: require("@/assets/images/stats/media-icon.svg"),
          },
          this.isAdaptive
            ? {
                title: " - ",
                info: this.$t("stats.course.cards.attention"),
                icon: require("@/assets/images/stats/alert-icon.svg"),
              }
            : undefined,
        ],
      },
      emptyState: {
        image: "empty-crawler.svg",
        title: this.$t("labels.generic", { pt: "Sem dados", en: "No data" }),
        description: this.$t("labels.generic", {
          pt: "Ainda não há dados suficientes para este curso",
          en: "No data available for this course",
        }),
      },
      students: new ChartData(),
      attention: new ChartData(),
      btnLoading: false as boolean,
      loading: false as boolean,
      csv: new GeneralInfo(),
      classroomSelected: "" as any,
      classrooms: [] as Classroom[],
      hasClassroom: false as boolean,
    };
  },
  created() {
    useThemeStore.getTheme();
  },
  mounted() {
    this.getData();
    this.getClasses(undefined);
  },
  watch: {
    classroomSelected(val, old) {
      if (val?.id || val?.id !== old?.id) this.getData();
    },
  },
  methods: {
    getData(): void {
      const courseId = String(this.$route.params.courseId);

      if (!useThemeStore.theme) useThemeStore.getTheme();
      this.service.general.subscribe({
        next: (response) => {
          if (!response?.id) return;
          this.mountChartStudents(response);
          this.mountChartAttention(response);
          this.csv = response;

          (this.data.cards as any) = [
            {
              title:
                response?.averageQuiz === 0 ? " - " : response?.averageQuiz,
              info: this.$t("stats.course.cards.quiz"),
              icon: require("@/assets/images/stats/media-icon.svg"),
            },
            this.isAdaptive
              ? {
                  title:
                    response?.totalUsersAlertedAttention === 0
                      ? " - "
                      : response?.totalUsersAlertedAttention,
                  info: this.$t("stats.course.cards.attention"),
                  icon: require("@/assets/images/stats/alert-icon.svg"),
                }
              : undefined,
          ];
        },
      });
      this.service.getGeneralFromCourse(courseId, {
        classroomId: this.classroomSelected?.id ?? undefined,
      });
    },
    mountChartStudents(response): void {
      const labels = {
        active: this.$t("stats.course.charts.titles.inProgress"),
        inactive: this.$t("stats.course.charts.titles.inactive"),
        concluded: this.$t("stats.course.charts.titles.conclude"),
      };

      const tooltips = {
        active: this.$t("stats.course.charts.tooltips.actives"),
        inactive: this.$t("stats.course.charts.tooltips.inactives"),
        concluded: this.$t("stats.course.charts.tooltips.concluded"),
      };

      this.students.series = [
        response?.totalUsersActive ?? 0,
        response?.totalUsersInactive ?? 0,
        response?.totalUsersFinished ?? 0,
      ];

      const options = {
        ...exportOptions.std,
        labels: [labels.active, labels.inactive, labels.concluded],
      };

      if (useThemeStore.theme?.colors) {
        options.colors = [
          useThemeStore.theme.colors["h-primary"][500],
          useThemeStore.theme.colors["h-secondary"][500],
          "#167F1A",
        ];
        options.fill = {
          colors: options.colors,
        };
      }

      this.students.options = options;

      this.students.legends = [
        new Legends(
          labels.active,
          response?.totalUsersActive ?? 0,
          tooltips.active,
          "bg-h-primary-400"
        ),
        new Legends(
          labels.inactive,
          response?.totalUsersInactive ?? 0,
          tooltips.inactive,
          "bg-h-error-50"
        ),
        new Legends(
          labels.concluded,
          response?.totalUsersFinished ?? 0,
          tooltips.concluded,
          "bg-h-success-300"
        ),
      ];
    },
    mountChartAttention(response): void {
      const labels = {
        active: this.$t("stats.course.charts.titles.enabled"),
        inactive: this.$t("stats.course.charts.titles.disabled"),
      };

      this.attention.series = [
        response?.totalUsersWithAttentionEnabled ?? 0,
        response?.totalUsersWithAttentionDisabled ?? 0,
      ];

      const options = {
        ...exportOptions.att,
        labels: [labels.active, labels.inactive],
      };

      if (useThemeStore.theme?.colors) {
        options.fill = {
          colors: [useThemeStore.theme.colors["h-primary"][500], "#97979B"],
        };
      }

      this.attention.options = options;

      this.attention.legends = [
        new Legends(
          labels.active,
          response?.totalUsersWithAttentionEnabled ?? 0,
          "",
          "bg-h-primary-500"
        ),
        new Legends(
          labels.inactive,
          response?.totalUsersWithAttentionDisabled ?? 0,
          "",
          "bg-h-neutral-400"
        ),
      ];
    },
    downloadCsv(): void {
      this.btnLoading = true;
      delete this.csv.id;
      const data = {
        course: this.data.course.name,
        active: this.csv.totalUsersActive,
        inactive: this.csv.totalUsersInactive,
        concluded: this.csv.totalUsersFinished,
        attEnabled: this.csv.totalUsersWithAttentionEnabled,
        attDisabled: this.csv.totalUsersWithAttentionDisabled,
        media: this.csv.averageQuiz,
        totalAttention: this.csv.totalUsersAlertedAttention,
        totalUsers: this.csv.totalUsers,
      };

      var csv = "";
      var arr = [colNames, { ...data }];
      for (var row of arr) {
        for (var prop in row) {
          var cellVal = row[prop];
          csv += '"' + cellVal + '", ';
        }
        csv += "\n";
      }

      setTimeout(() => {
        this.btnLoading = false;
        const blob = new Blob([csv], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        saveAs(url, `data_general.csv`);
      }, 1000);
    },
    getClasses(event): void {
      this.serviceClassrooms.classes.subscribe({
        next: (response: any) => {
          if (response?.content?.length === 0) return (this.classrooms = []);
          this.classrooms = response?.content;
          this.hasClassroom = response?.content?.length !== 0 ? true : false;
          StatsService.setHasClassroom(this.hasClassroom);
        },
      });
      this.serviceClassrooms.getAllClasses({
        page: 0,
        size: 10,
        name: event?.query,
        courseId: String(this.$route.params.courseId),
      });
    },
    setClassroomId(classroomId: string | undefined): void {
      StatsService.setClassroomId(classroomId!);
    },
  },
});
</script>
